import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

function overallPalette(status) {
  if (status === "good") return { color: "success", Icon: CheckCircleOutlineIcon };
  if (status === "attention") return { color: "warning", Icon: InfoOutlinedIcon };
  return { color: "error", Icon: ErrorOutlineIcon };
}

const WabaIntegrationStatus = () => {
  const workspaceId = useSelector(
    (s) => s.auth?.user?.workspaceId || s.workspace?.currentWorkspace?._id
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [activateOpen, setActivateOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [activateLoading, setActivateLoading] = useState(false);
  const [activateError, setActivateError] = useState("");

  const load = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      setError("No workspace selected.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setError("Not authenticated.");
        setLoading(false);
        return;
      }
      const { data } = await axios.get(
        `${SERVER}/api/workspaces/${workspaceId}/waba-integration`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayload(data);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to load integration status"
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitRegisterPhone = async () => {
    if (!/^\d{6}$/.test(pin)) {
      setActivateError("Enter exactly 6 digits (your WhatsApp Cloud API two-step PIN).");
      return;
    }
    setActivateError("");
    setActivateLoading(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setActivateError("Not authenticated.");
        return;
      }
      await axios.post(
        `${SERVER}/api/workspaces/${workspaceId}/waba-integration/register-phone`,
        { pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivateOpen(false);
      setPin("");
      await load();
    } catch (e) {
      setActivateError(
        e.response?.data?.message || e.message || "Activation request failed"
      );
    } finally {
      setActivateLoading(false);
    }
  };

  if (!workspaceId) return null;

  const checks = payload?.checks;
  const ov = checks?.overall;
  const { color: overallColor, Icon: OverallIcon } = overallPalette(ov?.status);

  return (
    <Box
      sx={{
        mt: 3,
        "& .MuiTypography-body2": { fontSize: "0.9375rem", lineHeight: 1.55 },
        "& .MuiTypography-body1": { fontSize: "1rem" },
        "& .MuiTypography-subtitle1": { fontSize: "1.0625rem" },
        "& .MuiTypography-subtitle2": { fontSize: "0.9375rem" },
        "& .MuiTypography-h6": { fontSize: "1.125rem" },
        "& .MuiTypography-caption": { fontSize: "0.8125rem" },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          WhatsApp integration
        </Typography>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={load}
          disabled={loading}
          variant="outlined"
        >
          Refresh status
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !payload && !error && (
        <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {payload?.success && checks && (
        <Stack spacing={2}>
          {/* Overall */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" alignItems="flex-start" gap={2}>
                <OverallIcon color={overallColor} sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {ov?.title || "Status"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {ov?.subtitle}
                  </Typography>
                  <Chip size="small" label={ov?.status?.toUpperCase() || "—"} color={overallColor} />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Business verification
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {checks.businessVerification?.label || "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Meta WABA field:{" "}
                    <strong>{checks.businessVerification?.wabaField || "—"}</strong>
                    {checks.businessVerification?.portfolioVerification && (
                      <>
                        {" "}
                        · Portfolio:{" "}
                        <strong>{checks.businessVerification.portfolioVerification}</strong>
                      </>
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    WABA account review
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {checks.wabaAccountReview?.label || "—"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Phone number
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      label={checks.phone?.connected ? "Connected" : "Not connected"}
                      color={checks.phone?.connected ? "success" : "default"}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={
                        checks.phone?.displayNameApproved
                          ? "Display name OK"
                          : "Display name pending"
                      }
                      color={checks.phone?.displayNameApproved ? "success" : "warning"}
                      variant="outlined"
                    />
                    {checks.phone?.accountMode && (
                      <Chip size="small" label={checks.phone.accountMode} variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {checks.phone?.summary}
                  </Typography>
                  {checks.phone?.graphStatus && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Meta status: <strong>{checks.phone.graphStatus}</strong>
                    </Typography>
                  )}
                  {payload.phoneDetail?.display_phone_number && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {payload.phoneDetail.display_phone_number}{" "}
                      {payload.phoneDetail.verified_name
                        ? `· ${payload.phoneDetail.verified_name}`
                        : ""}
                    </Typography>
                  )}
                  {checks.phone?.canActivateWithRegister && (
                    <Box sx={{ mt: 1.5 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          setActivateError("");
                          setPin("");
                          setActivateOpen(true);
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Activate number
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Calls the Meta Cloud API register step for the phone number ID stored on this workspace
                        (Graph{" "}
                        <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.9em" }}>
                          POST /{'{phone-number-id}'}/register
                        </Box>
                        ). You choose a 6-digit two-step PIN; store it safely, or enter the existing PIN from
                        WhatsApp Manager.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Messaging
                  </Typography>
                  <Chip
                    size="medium"
                    label={checks.messaging?.allowed ? "Allowed" : "Blocked / limited"}
                    color={checks.messaging?.allowed ? "success" : "error"}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {checks.messaging?.summary}
                  </Typography>
                  {checks.messaging?.blockers?.length > 0 && (
                    <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
                      {checks.messaging.blockers.map((b, i) => (
                        <li key={i}>
                          <Typography variant="body2">{b}</Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ClusterDen ↔ Meta connection */}
          <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: "action.hover" }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                ClusterDen connection
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={checks.connection?.tokenValid ? "Token OK" : "Token issue"}
                  color={checks.connection?.tokenValid ? "success" : "error"}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={checks.connection?.appSubscribed ? "App subscribed" : "App not subscribed"}
                  color={checks.connection?.appSubscribed ? "success" : "warning"}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={
                    checks.connection?.hasMessagingPermission
                      ? "Messaging permission"
                      : "No messaging permission"
                  }
                  color={checks.connection?.hasMessagingPermission ? "success" : "warning"}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Next steps */}
          {payload.actions?.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Next steps
              </Typography>
              <Stack spacing={1.5}>
                {payload.actions.map((a, i) => (
                  <Card key={i} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {a.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {a.description}
                      </Typography>
                      {a.href && (
                        <Button
                          component={Link}
                          href={a.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          variant="contained"
                          sx={{ textTransform: "none" }}
                        >
                          {a.buttonLabel || "Open"}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          {payload.errors?.length > 0 && (
            <Alert severity="warning">
              {payload.errors.map((e, i) => (
                <div key={i}>
                  <strong>{e.source}:</strong> {e.message}
                </div>
              ))}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" display="block">
            Data from Meta Graph for this workspace. After changing settings in Meta, click{" "}
            <strong>Refresh status</strong>.
          </Typography>
        </Stack>
      )}

      <Dialog
        open={activateOpen}
        onClose={() => !activateLoading && setActivateOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>Activate WhatsApp number</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a 6-digit PIN. Meta stores this as two-step verification for the number; keep it somewhere
            safe. If the number already has a PIN in WhatsApp Manager, use that same PIN.
          </Typography>
          <TextField
            autoFocus
            label="6-digit PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            fullWidth
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            disabled={activateLoading}
            sx={{ "& .MuiInputBase-input": { fontSize: "1.1rem", letterSpacing: 2 } }}
          />
          {activateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {activateError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateOpen(false)} disabled={activateLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitRegisterPhone}
            disabled={activateLoading || pin.length !== 6}
          >
            {activateLoading ? <CircularProgress size={22} /> : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WabaIntegrationStatus;
